/**
 * Currency parsing utility for Orders.
 *
 * The backend stores `order.currency` as one of:
 *   - A JSON string: '{"symbol":"NGN","rate":1500}'
 *   - A plain currency code string: 'USD'
 *   - An empty stringified object: '{}'
 *   - undefined / null
 *
 * Without proper guards, calling JSON.parse on any of these can crash
 * the entire orders page. This util safely extracts the symbol and rate
 * with defensive defaults.
 *
 * Usage:
 *   const { symbol, rate } = parseOrderCurrency(order);
 */

const DEFAULT_SYMBOL = 'USD';
const DEFAULT_RATE   = 1;

/**
 * Safely parse a JSON string. Returns null on failure (no throw).
 */
const safeJsonParse = (str) => {
    if (typeof str !== 'string') return null;
    try {
        return JSON.parse(str);
    } catch {
        return null;
    }
};

/**
 * Extract symbol + rate from an order's currency field.
 *
 * @param {Object} order
 * @returns {{ symbol: string, rate: number }}
 */
export const parseOrderCurrency = (order) => {
    const raw = order?.currency;

    /* Nothing provided → defaults */
    if (raw == null || raw === '' || raw === '{}') {
        return { symbol: DEFAULT_SYMBOL, rate: DEFAULT_RATE };
    }

    /* Plain string code (e.g. 'NGN', 'USD') */
    if (typeof raw === 'string' && !raw.includes('{')) {
        return { symbol: raw || DEFAULT_SYMBOL, rate: DEFAULT_RATE };
    }

    /* JSON-encoded object */
    const parsed = safeJsonParse(raw);
    if (parsed && typeof parsed === 'object') {
        const symbol = typeof parsed.symbol === 'string'
            ? parsed.symbol
            : DEFAULT_SYMBOL;
        const rate = Number(parsed.rate);
        return {
            symbol,
            rate: Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_RATE,
        };
    }

    /* Object passed directly (not a string) */
    if (raw && typeof raw === 'object') {
        const symbol = typeof raw.symbol === 'string' ? raw.symbol : DEFAULT_SYMBOL;
        const rate = Number(raw.rate);
        return {
            symbol,
            rate: Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_RATE,
        };
    }

    return { symbol: DEFAULT_SYMBOL, rate: DEFAULT_RATE };
};

/**
 * Safely parse any JSON field from an order with a fallback.
 *
 * @param {string} jsonString
 * @param {*} fallback
 */
export const parseOrderJson = (jsonString, fallback = null) => {
    if (jsonString == null || jsonString === '') return fallback;
    if (typeof jsonString === 'object') return jsonString;
    return safeJsonParse(jsonString) ?? fallback;
};

/**
 * Format a numeric amount with currency symbol.
 *
 * @param {number|string} amount
 * @param {string} symbol
 * @returns {string}
 */
export const formatOrderAmount = (amount, symbol = DEFAULT_SYMBOL) => {
    const num = Number(amount);
    if (!Number.isFinite(num)) return `${symbol} 0.00`;
    return `${symbol} ${num.toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};